// src/pages/Cart.tsx
import React from 'react';
import { Minus, Plus, X, ShoppingCart } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { toast } from 'react-hot-toast';

interface CartProps {
  onCheckout: () => void;
  onClose: () => void;
}

export const Cart = ({ onCheckout, onClose }: CartProps) => {
  const { state, dispatch } = useApp();

  // Backend base URL
  const backendBaseURL = import.meta.env.VITE_API_BASE_URL;
  const backendBaseImagesURL = import.meta.env.VITE_API_BASE_IMAGES_URL;

  // Helper function to get full image URL
  const getFullImageURL = (url: string): string => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${backendBaseImagesURL}${url}`;
  };

  // Compute a composite key for a cart item based on product id and its selected variations.
  const computeItemKey = (item: any): string => {
    return `${item.id}${item.selectedVariations ? '-' + JSON.stringify(item.selectedVariations) : ''}`;
  };

  // Given a cart item, return its available stock.
  // For simple products, use item.stock.
  // For products with variations (and a parsed variations.stockByVariation array),
  // try to find the matching variation row based on the keys in item.selectedVariations.
  const getAvailableStock = (item: any): number => {
    if (!item.variations || !item.selectedVariations) return item.stock;

    const variationType = item.variations.type; // e.g., "color-age"
    
    if (variationType === 'color-age') {
      const { color, ageRange } = item.selectedVariations;
    
      if (color) {
        // Find the parent variation based on selected color
        const colorVar = item.variations.stockByVariation.find(
          (v: any) => v.color === color && v.parentId === null
        );
        if (colorVar) {
          if (ageRange) {
            // Find the child variation based on selected ageRange
            const ageVar = item.variations.stockByVariation.find(
              (v: any) => v.parentId === colorVar.id && v.ageRange === ageRange
            );
            if (ageVar) {
              return ageVar.stock;
            }
          }
          // If no ageRange is selected, return the parent color variation stock
          return colorVar.stock;
        }
      }
    }

    // Add similar logic here if you have other variation types like "color-size" or "age-size"

    // Fallback to product's overall stock if no specific variation is matched
    return item.stock;
  };

  // Helper function to get the cart item image based on selected variations.
  const getCartItemImage = (item: any): string => {
    // If there are no images, return a default image path
    if (!item.images || item.images.length === 0) {
      return '/path/to/default-image.jpg';
    }
    
    // If the item has a selected color variation, find the color-specific image
    if (item.selectedVariations?.color && item.variations && item.images) {
      const { color } = item.selectedVariations;
      
      // Find the variation row corresponding to the selected color
      const colorVariation = item.variations.stockByVariation?.find(
        (v: any) => v.color === color && v.parentId === null
      );
      
      // If we find a matching color variation
      if (colorVariation) {
        // Filter for images whose variation_id matches that color-level variation
        const variationImages = item.images.filter(
          (img: any) => img.variation_id === parseInt(colorVariation.id)
        );
        
        // If we found color-specific images, use the first one
        if (variationImages.length > 0) {
          return getFullImageURL(variationImages[0].url);
        }
      }
    }
    
    // Fallback to the first product image if no variation-specific image was found
    return getFullImageURL(item.images[0].url);
  };

  // Update quantity with variation-level stock validation.
  // We now pass the composite key as the identifier.
  const updateQuantity = (itemKey: string, newQuantity: number) => {
    if (newQuantity < 1) return;

    // Locate the cart item by matching its computed key.
    const cartItem = state.cart.find((item: any) => computeItemKey(item) === itemKey);
    if (cartItem) {
      const availableStock = getAvailableStock(cartItem);
      if (newQuantity > availableStock) {
        toast.error(`Only ${availableStock} items are available for the selected variation.`);
        return;
      }
      dispatch({
        type: 'UPDATE_QUANTITY',
        payload: { id: itemKey, quantity: newQuantity },
      });
      toast.success('Cart updated');
    }
  };

  // Remove a cart item using its composite key.
  const removeFromCart = (itemKey: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: itemKey });
    toast.success('Item removed from cart');
  };

  // Compute the cart total
  const total = state.cart.reduce(
    (sum: number, item: any) => sum + item.price * item.quantity,
    0
  );

  const generateOrderNumber = () => {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = 'WAPP';
    for (let i = 0; i < 4; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const formatDate = () => {
    const now = new Date();
    return now.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const generateWhatsAppMessage = () => {
    const items = state.cart
      .map((item: any, index: number) =>
        `${index + 1}. ${item.name} (${item.quantity}x) - KES ${(item.price * item.quantity).toLocaleString()}${
          item.selectedVariations
            ? `\n   Variations: ${Object.entries(item.selectedVariations)
                .map(([key, value]) => `${key}: ${value}`)
                .join(', ')}`
            : ''
        }`
      )
      .join('\n\n');

    const message = `Hello Shynerz Team, I would like to order the following items: \n\n${items}\n\nTotal: KES ${total.toLocaleString()}\n\nI would like you to:\ni. Confirm availability\nii. Send Payment details\n\nThank you\n\nDate: ${formatDate()}\nOrder Number: ${generateOrderNumber()}`;

    return encodeURIComponent(message);
  };

  if (state.cart.length === 0) {
    return (
      <div className="fixed right-0 top-16 w-96 h-[calc(100vh-4rem)] bg-white shadow-xl p-6 transform transition-transform z-50">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Shopping Cart</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-gray-500">Your cart is empty</p>
      </div>
    );
  }

  return (
    <div className="fixed right-0 top-16 w-96 h-[calc(100vh-4rem)] bg-white shadow-xl p-6 transform transition-transform z-50 overflow-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Shopping Cart</h2>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="space-y-4">
        {state.cart.map((item: any) => {
          const itemKey = computeItemKey(item);
          return (
            <div key={itemKey} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
              <img
                src={getCartItemImage(item)}
                alt={item.name}
                className="w-20 h-20 object-cover rounded-md"
              />
              <div className="flex-1">
                <h3 className="font-semibold">{item.name}</h3>
                <p className="text-pink-500 font-bold">KES {item.price.toLocaleString()}</p>
                {item.selectedVariations && (
                  <div className="text-sm text-gray-600 mt-1">
                    {item.selectedVariations.color && (
                      <span className="mr-2">Color: {item.selectedVariations.color}</span>
                    )}
                    {item.selectedVariations.size && (
                      <span className="mr-2">Size: {item.selectedVariations.size}</span>
                    )}
                    {item.selectedVariations.ageRange && (
                      <span>Age: {item.selectedVariations.ageRange}</span>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => updateQuantity(itemKey, item.quantity - 1)}
                    className="p-1 rounded-full hover:bg-gray-200"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-8 text-center">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(itemKey, item.quantity + 1)}
                    className="p-1 rounded-full hover:bg-gray-200"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => removeFromCart(itemKey)}
                    className="ml-auto p-1 rounded-full hover:bg-gray-200 text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 border-t pt-4">
        <div className="flex justify-between items-center mb-4">
          <span className="font-semibold">Total:</span>
          <span className="text-xl font-bold text-pink-500">KES {total.toLocaleString()}</span>
        </div>
        <div className="space-y-2">
          <button
            onClick={onCheckout}
            className="w-full bg-pink-500 text-white py-3 rounded-full font-semibold hover:bg-pink-600 transition-colors"
          >
            Proceed to Checkout
          </button>
          <a
            href={`https://wa.me/254759409139?text=${generateWhatsAppMessage()}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-green-500 text-white py-3 rounded-full font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Order via WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default Cart;
